import { type ReactNode, useRef } from 'react';
import {
  useDispatch,
  useLead,
  useLeadsFromIdArray,
} from '@witmetrics/data-store';
import type { UndecoratedLead } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { useArray } from '@/hooks/useArray';
import SearchField from '@/components/inputs/SearchField';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import LeadListItem from './LeadListItem';
import { classes, fetchMatchingLeads } from './utils';

export type ExistingLeadFormProps = {
  leadId?: number;
  onSelect: (leadId: number | undefined) => void;
};

export default function ExistingLeadForm({
  leadId,
  onSelect,
}: ExistingLeadFormProps) {
  // TODO: Grab recentLeads from appState
  const timestampRef = useRef(Date.now());
  const dispatch = useDispatch();
  const { onApiError, recentLeadIds } = useAppState();
  const activeLead = useLead(leadId);
  const recentLeads = useLeadsFromIdArray(recentLeadIds);
  const [search, setSearch] = useString();
  const [suggestions, setSuggestions] = useArray<UndecoratedLead>();
  const [isSearching, toggleSearching] = useToggle();

  const handleSearch = async (query: string) => {
    try {
      setSearch(query);
      toggleSearching(true);
      const { leads, timestamp } = await fetchMatchingLeads(query);
      const updates = timestamp > timestampRef.current ? leads : [];
      timestampRef.current = timestamp;
      setSuggestions(updates);
      dispatch.addLeads(leads);
      toggleSearching(false);
    } catch (err) {
      toggleSearching(false);
      onApiError(err, 'Error finding patients');
    }
  };

  const renderList = () => {
    const showRecents = isEmpty(search);
    let items = showRecents ? recentLeads : suggestions;
    let content: ReactNode;
    if (isSearching) {
      content = (
        <div className={classes.message}>
          Searching
          <LoopingEllipsis />
        </div>
      );
    } else if (isEmpty(items)) {
      content = <div className={classes.message}>No matching patients</div>;
    } else {
      content = (
        <ul className={classes.list}>
          {items.map(({ id }) => (
            <LeadListItem key={id} leadId={id} onSelect={() => onSelect(id)} />
          ))}
        </ul>
      );
    }
    return (
      <div className={classes.listWrapper}>
        <h3 className={classes.listTitle}>
          {showRecents && !isSearching
            ? 'Recent Patients'
            : 'Matching Patients'}
        </h3>
        {content}
      </div>
    );
  };

  if (activeLead) {
    return (
      <div>
        <ul>
          <LeadListItem
            active
            leadId={activeLead.id}
            onSelect={() => onSelect(undefined)}
          />
        </ul>
      </div>
    );
  }

  return (
    <div>
      <SearchField
        className={classes.searchField}
        label="Search"
        value={search}
        onChange={(s) => handleSearch(s)}
      />
      {renderList()}
    </div>
  );
}
